import React, { useEffect, useState } from 'react'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    useTheme,
} from '@material-ui/core'
import { toast } from 'react-toastify'
import { axiosInstance } from '../../../helpers'
import { Container } from '../../../components'

interface Props {
    open: boolean
    onClose: () => void
    vendorId: number
    vendorEmail: string
    defaultReplyTo: string
    defaultFromName: string
}

export const SendVendorEmailDialog = (props: Props) => {
    const {
        open,
        onClose,
        vendorId,
        vendorEmail,
        defaultReplyTo,
        defaultFromName,
    } = props
    const theme = useTheme()
    const [fromName, setFromName] = useState('')
    const [replyTo, setReplyTo] = useState('')
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [sending, setSending] = useState(false)

    useEffect(() => {
        if (!open) {
            return
        }
        setFromName(defaultFromName)
        setReplyTo(defaultReplyTo)
        setSubject('')
        setBody('')
    }, [open, defaultFromName, defaultReplyTo])

    const canSend =
        fromName.trim().length > 0 &&
        replyTo.trim().length > 0 &&
        subject.trim().length > 0 &&
        body.trim().length > 0 &&
        vendorEmail.trim().length > 0

    const handleSend = async () => {
        if (!canSend || sending) {
            return
        }
        setSending(true)
        try {
            await axiosInstance.post(
                `company/national_vendor_list/${vendorId}/send_email/`,
                {
                    subject: subject.trim(),
                    body: body.trim(),
                    reply_to: replyTo.trim(),
                    from_name: fromName.trim(),
                },
            )
            toast.success('Email sent')
            onClose()
        } catch {
            toast.error('Failed to send email')
        } finally {
            setSending(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={sending ? undefined : onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Send Email</DialogTitle>
            <DialogContent>
                <Container
                    direction="column"
                    style={{
                        gap: theme.spacing(2),
                        paddingTop: theme.spacing(0.5),
                    }}
                >
                    <span
                        style={{
                            color: theme.palette.grey[700],
                            fontSize: 14,
                            lineHeight: '20px',
                        }}
                    >
                        To: {vendorEmail}
                    </span>
                    <TextField
                        label="Message From"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={fromName}
                        onChange={(event) => setFromName(event.target.value)}
                        disabled={sending}
                    />
                    <TextField
                        label="Reply-To"
                        variant="outlined"
                        size="small"
                        type="email"
                        fullWidth
                        value={replyTo}
                        onChange={(event) => setReplyTo(event.target.value)}
                        disabled={sending}
                    />
                    <TextField
                        label="Subject"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={subject}
                        onChange={(event) => setSubject(event.target.value)}
                        disabled={sending}
                    />
                    <TextField
                        label="Body"
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={8}
                        value={body}
                        onChange={(event) => setBody(event.target.value)}
                        disabled={sending}
                    />
                </Container>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={sending}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSend}
                    disabled={sending || !canSend}
                >
                    {sending ? <CircularProgress size={20} /> : 'Send'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
